import {
  CoreWebWrapper,
  LocalizationWrapper,
  ToastProvider,
  createCoreWebWrapperOptions,
  i18nCore,
} from '@core-frontend/core-frontend';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import {
  RouterProvider,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

import { baseClient } from '@/shared/api/baseClient';
import '@/shared/lib/domain/isCurrentHostEqualsCore';

import { appRouter } from './app-router';
import GlobalOverrides from './global-styles/GlobalOverrides';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
    }),
    Sentry.httpClientIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [import.meta.env.VITE_API_URL, /^\/api\//],
  environment: import.meta.env.MODE,
  // Session Replay
  replaysSessionSampleRate: import.meta.env.MODE !== 'dev' ? 0 : 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: import.meta.env.MODE !== 'dev' ? 0 : 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const defaultOptions = createCoreWebWrapperOptions();
const queryClient = new QueryClient();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nextProvider i18n={i18nCore}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <GlobalOverrides />
        <CoreWebWrapper options={defaultOptions} baseClient={baseClient}>
          <QueryClientProvider client={queryClient}>
            <LocalizationWrapper>
              <ToastProvider>
                <RouterProvider router={appRouter} />
              </ToastProvider>
            </LocalizationWrapper>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </CoreWebWrapper>
      </LocalizationProvider>
    </I18nextProvider>
  </StrictMode>
);
